import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { isEmpty, reverse } from 'ramda';
import { isNilOrEmpty, isObject } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import { getLabelMessage } from '@gef-ui/localization/messages';

import ComponentLoader from 'components/molecules/ComponentLoader';
import { urls } from 'features/common/fetch/constants';
import {
	fetchSubordinatesForReportModal,
	fetchSuperiorsForReportModal,
	resetReportModal,
	startupModal,
} from 'features/views/productionControl/reportProductionControl/actions';
import {
	FIELDS,
	REPORT_PRODUCTION_CONTROL_FORM_NAME,
} from 'features/views/productionControl/reportProductionControl/form';
import {
	getCheckedWavesIds,
	getReportModalSubordinates,
	getReportModalSuperiors,
} from 'features/views/productionControl/reportProductionControl/selectors';
import { setCheckedWave } from 'features/views/productionControl/reportProductionControl/slice';
import { getRegions, getWaves } from 'features/views/productionControl/selectors';

const ReportProductionControlBody = ({ onConfirm }) => {
	const dispatch = useDispatch();
	const formData = useFormData();
	const next = useControlNext(REPORT_PRODUCTION_CONTROL_FORM_NAME);

	const formIsInitiated = isObject(formData) && isEmpty(formData);

	const waves = useSelector(getWaves);
	const oldestToNewestWaves = reverse(waves);

	const regions = useSelector(getRegions);
	const superiors = useSelector(getReportModalSuperiors);
	const subordinates = useSelector(getReportModalSubordinates);
	const checkedWavesId = useSelector(getCheckedWavesIds);

	useEffect(() => {
		if (!isNilOrEmpty(regions) && formIsInitiated) {
			dispatch(startupModal());
		}
	}, [dispatch, regions, formIsInitiated]);

	useEffect(
		() => () => {
			dispatch(resetReportModal());
		},
		[dispatch],
	);

	// resetting possible previous values
	const resetFields = useCallback(
		(level) => {
			const fields = ['region', 'superior', 'subordinate'];
			const fieldsToBeReseted = fields.slice(level);

			fieldsToBeReseted.forEach((fieldName) => next(setValue({ fieldName, value: null })));
		},
		[next],
	);

	const handleChangeCheckbox = useCallback(
		(id) => () => {
			// any change in waves = reset to region level and possible refetch of superiors
			dispatch(setCheckedWave(id));

			resetFields(1);
			dispatch(fetchSuperiorsForReportModal());
		},
		[dispatch, resetFields],
	);

	const getCheckedValueById = (id) => {
		return checkedWavesId.some((item) => item === id);
	};

	const handleRegionChange = useCallback(
		({ level }) =>
			() => {
				resetFields(level);
				dispatch(fetchSuperiorsForReportModal());
			},
		[dispatch, resetFields],
	);

	const handleSuperiorChange = useCallback(
		({ level }) =>
			() => {
				resetFields(level);
				dispatch(fetchSubordinatesForReportModal());
			},
		[dispatch, resetFields],
	);

	const handleExport = useCallback(() => {
		onConfirm();
	}, [onConfirm]);

	return (
		<Grid container spacing={3}>
			<Grid size={12}>
				<Stack spacing={1}>
					<Typography variant="h6" color="text.primary">
						{getLabelMessage('productionControl:wave')}
					</Typography>
					<FormGroup>
						{oldestToNewestWaves.map((item) => (
							<FormControlLabel
								control={
									<Checkbox
										checked={getCheckedValueById(item.id)}
										onChange={handleChangeCheckbox(item.id)}
									/>
								}
								label={item.description}
							/>
						))}
					</FormGroup>
				</Stack>
			</Grid>
			<Grid size={12}>
				<Grid container spacing={2}>
					<Grid size={4}>
						<ComponentLoader fetchStorePath={urls.koprRegion}>
							<Autocomplete
								size="small"
								fieldName={FIELDS.region}
								label={getLabelMessage('productionControl:region')}
								options={regions}
								disabled={regions.length <= 1}
								onChange={handleRegionChange({ level: 1 })}
								labelPropName="description"
							/>
						</ComponentLoader>
					</Grid>
					<Grid size={4}>
						<ComponentLoader fetchStorePath={urls.koprSuperiors}>
							<Autocomplete
								size="small"
								fieldName={FIELDS.superior}
								label={getLabelMessage('productionControl:superior')}
								options={superiors}
								disabled={isNilOrEmpty(formData?.region) || isEmpty(checkedWavesId)}
								labelPropName="description"
								onChange={handleSuperiorChange({ level: 2 })}
							/>
						</ComponentLoader>
					</Grid>
					<Grid size={4}>
						<ComponentLoader fetchStorePath={urls.koprSubordinates}>
							<Autocomplete
								size="small"
								fieldName={FIELDS.subordinate}
								label={getLabelMessage('productionControl:subordinate')}
								options={subordinates}
								disabled={isNilOrEmpty(formData?.superior) || isEmpty(checkedWavesId)}
								labelPropName="description"
							/>
						</ComponentLoader>
					</Grid>
				</Grid>
			</Grid>
			<Grid size={12}>
				<Grid
					container
					spacing={3}
					sx={{
						justifyContent: 'flex-end',
					}}
				>
					<Grid size={3}>
						<ComponentLoader fetchStorePath={urls.koprReport} ignoreErrors>
							<Button
								variant="contained"
								onClick={handleExport}
								disabled={isEmpty(checkedWavesId)}
								fullWidth
							>
								{getLabelMessage('productionControl:report')}
							</Button>
						</ComponentLoader>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

ReportProductionControlBody.propTypes = {
	onConfirm: PropTypes.func.isRequired,
};

export default ReportProductionControlBody;
